import * as React from 'react';
import { useToast } from '@fragments-sdk/ui';
import { useMutation } from 'convex/react';
import { api, type Id } from '@/convex';
import { formatCompactCount, plural } from '../findings-display-model';
import type { PrActionState, PrDecisionDetail } from '../findings-types';

export function usePrReviewActions({
  selectedProjectId,
  orgId,
  activePrNumber,
  selectedDecisionKey,
}: {
  selectedProjectId?: Id<'projects'>;
  orgId?: Id<'orgs'> | null;
  activePrNumber: number | null;
  selectedDecisionKey: string | null;
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const recordDecisionAction = useMutation(api.findingsPr.recordDecisionAction);
  const [actionState, setActionState] = React.useState<PrActionState>({
    status: 'idle',
  });

  React.useEffect(() => {
    setActionState({ status: 'idle' });
  }, [selectedDecisionKey, activePrNumber]);

  async function recordPrDecisionAction(
    currentDetail: PrDecisionDetail,
    kind: 'approved' | 'requested_fix' | 'autofix_created' | 'commented',
    metadata?: Record<string, unknown>,
  ) {
    if (!selectedProjectId || !activePrNumber) return;
    await recordDecisionAction({
      projectId: selectedProjectId,
      prNumber: activePrNumber,
      decisionKey: currentDetail.decision.key,
      kind,
      ...(metadata ? { metadataJson: JSON.stringify(metadata) } : {}),
    });
  }

  async function handleCreatePrFix(currentDetail: PrDecisionDetail) {
    if (!selectedProjectId || !orgId) return;
    const findingIds = currentDetail.decision.safeFixFindingIds;
    if (findingIds.length === 0) {
      toastError('No safe fixes', 'This decision needs review before autofix.');
      return;
    }
    setActionState({ status: 'running', action: 'autofix' });
    try {
      const res = await fetch('/api/govern/autofix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgId,
          projectId: selectedProjectId,
          findingIds,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        prUrl?: string;
        prNumber?: number;
        fixedCount?: number;
        error?: string;
      };
      if (!res.ok || !data.ok) {
        const message = data.error ?? 'Fix PR could not be created.';
        setActionState({ status: 'error', action: 'autofix', message });
        toastError('Fix PR failed', message);
        return;
      }
      const fixedCount = data.fixedCount ?? findingIds.length;
      setActionState({
        status: 'success',
        action: 'autofix',
        message: `Created PR #${data.prNumber ?? ''} with ${formatCompactCount(fixedCount)} fix${fixedCount === 1 ? '' : 'es'}.`,
        prUrl: data.prUrl,
      });
      await recordPrDecisionAction(currentDetail, 'autofix_created', {
        prUrl: data.prUrl,
        prNumber: data.prNumber,
        fixedCount,
      });
      toastSuccess('Fix PR created');
    } catch {
      const message = 'Network error while creating the fix PR.';
      setActionState({ status: 'error', action: 'autofix', message });
      toastError('Fix PR failed', message);
    }
  }

  async function handlePostPrComments(currentDetail: PrDecisionDetail) {
    if (!selectedProjectId || !orgId || !activePrNumber) return;
    const findingIds =
      currentDetail.decision.openFindingIds.length > 0
        ? currentDetail.decision.openFindingIds
        : currentDetail.decision.representativeFindingIds;
    if (findingIds.length === 0) {
      toastError('No findings to comment on');
      return;
    }
    setActionState({ status: 'running', action: 'comments' });
    try {
      const res = await fetch('/api/govern/pr-comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgId,
          projectId: selectedProjectId,
          prNumber: activePrNumber,
          findingIds,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        postedCount?: number;
        message?: string;
        error?: string;
      };
      if (!res.ok || !data.ok) {
        const message = data.error ?? 'PR comments could not be posted.';
        setActionState({ status: 'error', action: 'comments', message });
        toastError('Comments failed', message);
        return;
      }
      const message =
        data.message ??
        `Posted ${plural(data.postedCount ?? 0, 'comment')} to the pull request.`;
      setActionState({ status: 'success', action: 'comments', message });
      await recordPrDecisionAction(currentDetail, 'commented', {
        postedCount: data.postedCount ?? 0,
      });
      toastSuccess('PR comments posted');
    } catch {
      const message = 'Network error while posting PR comments.';
      setActionState({ status: 'error', action: 'comments', message });
      toastError('Comments failed', message);
    }
  }

  async function handleApproveDecision(currentDetail: PrDecisionDetail) {
    setActionState({ status: 'running', action: 'review' });
    try {
      await recordPrDecisionAction(currentDetail, 'approved');
      setActionState({
        status: 'success',
        action: 'review',
        message: 'Decision approved for this PR run.',
      });
      toastSuccess('Decision approved');
    } catch {
      const message = 'Could not save the reviewer decision.';
      setActionState({ status: 'error', action: 'review', message });
      toastError('Decision not saved', message);
    }
  }

  async function handleRequestFixDecision(currentDetail: PrDecisionDetail) {
    setActionState({ status: 'running', action: 'review' });
    try {
      await recordPrDecisionAction(currentDetail, 'requested_fix');
      setActionState({
        status: 'success',
        action: 'review',
        message: 'Fix requested for this PR decision.',
      });
      toastSuccess('Fix requested');
    } catch {
      const message = 'Could not save the reviewer decision.';
      setActionState({ status: 'error', action: 'review', message });
      toastError('Decision not saved', message);
    }
  }

  return {
    actionState,
    handleCreatePrFix,
    handlePostPrComments,
    handleApproveDecision,
    handleRequestFixDecision,
  };
}
